import React from 'react';

import { Link as RouterLink } from '~/lib/react-router-dom';

import { Box, Link, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';

import { BidHistoryRecord } from '~/services/PublicOfferFeedService';

import dateFormatter from '~/utils/dateFormatter';

const columns: Array<GridColDef> = [
  {
    field: 'customerId',
    headerName: 'Account Id',
    flex: 1,
  },
  {
    field: 'campaignId',
    headerName: 'Campaign Id',
    flex: 1,
    renderCell: (params) => (
      <Link
        component={RouterLink}
        to={`/marketing/google-ads/customer/${params.row.customerId}/campaign/${params.row.campaignId}`}
        underline="hover"
      >
        {params.row.campaignId}
      </Link>
    ),
  },
  {
    field: 'adGroupId',
    headerName: 'Ad Group Id',
    flex: 1,
    renderCell: (params) => (
      <Link
        component={RouterLink}
        to={`/marketing/google-ads/customer/${params.row.customerId}/campaign/${params.row.campaignId}/ad-group/${params.row.adGroupId}`}
        underline="hover"
      >
        {params.row.adGroupId}
      </Link>
    ),
  },
  {
    field: 'difference',
    headerName: 'Target Difference %',
    flex: 1,
    align: 'right',
    headerAlign: 'right',
    renderCell: (params) => {
      const targetDiff =
        params.row.differencePercentage ??
        ((params.row.target - params.row.previousTarget) * 100) / params.row.previousTarget;
      return (
        <Typography fontSize={16} color={targetDiff > 0 ? 'green' : 'red'}>
          {targetDiff > 0 ? '+' : ''}
          {Number(targetDiff).toFixed(2)}%
        </Typography>
      );
    },
  },
  {
    field: 'target',
    headerName: 'Target',
    flex: 1,
    align: 'right',
    headerAlign: 'right',
  },
  {
    field: 'previousTarget',
    headerName: 'Previous Target',
    flex: 1,
    align: 'right',
    headerAlign: 'right',
  },
  {
    field: 'biddingStrategy',
    headerName: 'bidding Strategy',
    align: 'right',
    headerAlign: 'right',
    flex: 1,
  },
  {
    field: 'biddingController',
    headerName: 'Controller',
    flex: 1,
    align: 'right',
    headerAlign: 'right',
    renderCell: (params) => params.row.biddingController ?? '-',
  },
  {
    field: 'changedBy',
    headerName: 'Changed By',
    flex: 2,
    headerAlign: 'right',
    align: 'right',
    renderCell: (params) => params.row.changedBy ?? 'Daily Automation',
  },
  {
    field: 'updatedAt',
    headerName: 'Updated At',
    flex: 1,
    headerAlign: 'right',
    align: 'right',
    renderCell: (params) => dateFormatter(params.row.updatedAt),
  },
];

export type BidModificationHistoryTablePaginationModel = GridPaginationModel;

export type BidModificationHistoryTableProps = {
  rows: Array<BidHistoryRecord>;
  isLoading: boolean;
  rowCount: number;
  paginationModel: BidModificationHistoryTablePaginationModel;
  onPaginationModelChange: (model: BidModificationHistoryTablePaginationModel) => void;
  pageSizeOptions?: Array<number>;
};

function getRowId(row: BidHistoryRecord): string {
  // Prefer backend-provided `id` if present, otherwise create a stable-ish key from fields we display.
  const anyRow = row as unknown as { id?: string | number };
  if (anyRow?.id !== undefined && anyRow.id !== null) {
    return String(anyRow.id);
  }

  return [
    row.customerId,
    row.campaignId,
    row.adGroupId,
    row.updatedAt,
    row.target,
    row.previousTarget,
    row.biddingStrategy,
    row.changedBy,
  ]
    .map((v) => String(v ?? ''))
    .join('|');
}

export default function BidModificationHistoryTable({
  rows,
  isLoading,
  rowCount,
  paginationModel,
  onPaginationModelChange,
  pageSizeOptions = [100, 250, 500],
}: BidModificationHistoryTableProps) {
  return (
    <Box mt={2}>
      <DataGrid
        slots={{ pagination: GridPagination }}
        loading={isLoading}
        rows={rows}
        getRowId={getRowId}
        columns={columns}
        autoHeight
        rowCount={rowCount}
        paginationMode="server"
        pageSizeOptions={pageSizeOptions}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
      />
    </Box>
  );
}
